// src/CqcCompliance.js
// =============================================================================
// CQC Compliance dashboard — the sister-app equivalent of
// RegulatoryCompliance.js, same four-level drill-down:
//
//   KEY QUESTION (tab)  →  QUALITY STATEMENT  →  SITE  →  EVIDENCE CATEGORY
//
// Percentages roll up from the evidence-category level exactly as they do in
// the pharmacy app (see cqcTaxonomy.js header comment for the full mapping).
//
// STATUS SOURCE (documented the way the original marks its PLACEHOLDER seam):
// an evidence category counts as "covered" for a site if an attestation has
// been recorded for it (a data source has been pointed at — spreadsheet,
// Word doc, database link, existing system, or a manual statement). This
// scaffold does not yet pull *live* per-element status the way the pharmacy
// app wires individual GPhC elements to real endpoints (near-miss log,
// task grid, etc.) — every CQC element currently needs an explicit evidence
// source recorded via CqcEvidenceDialog. That's the natural next slice of
// work once specific sites' real data sources (rota system, incident
// database, training records...) are identified — wire each one the same
// way LIVE_SOURCES/EXTRA_SOURCES do in RegulatoryCompliance.js, and prefer
// the live read the way resolveDone() there prefers a live source over an
// attestation.
// =============================================================================

import React, { useEffect, useMemo, useState } from "react";
import { GROUPS } from "./cqcTaxonomy";
import { cqcApi } from "./cqcApi";
import { pctBg, pctBgStrong, fmtPct, RatingBadge, ReqBadge } from "./cqcColors";
import CqcEvidenceDialog from "./CqcEvidenceDialog";
import {
  attestationIndex, itemPctForSite, itemPctAcrossSites, groupPctAcrossSites,
  applicabilityIndex, itemApplicableForSite,
} from "./cqcRollup";

const S = {
  wrap: { fontFamily: "system-ui, sans-serif", padding: "16px 4px" },
  header: { display: "flex", alignItems: "baseline", gap: 10, marginBottom: 4 },
  h1: { fontSize: 20, fontWeight: 700, margin: 0 },
  sub: { fontSize: 12.5, color: "#6b7280", marginBottom: 14 },
  tabs: { display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 },
  tab: (active) => ({
    padding: "8px 14px", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer",
    border: active ? "2px solid #1e293b" : "1px solid #e5e7eb",
  }),
  crumbs: { fontSize: 12.5, color: "#6b7280", marginBottom: 10, cursor: "default" },
  crumbLink: { color: "#2563eb", cursor: "pointer" },
  list: { display: "flex", flexDirection: "column", gap: 6 },
  row: { display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 8,
         border: "1px solid #e5e7eb", cursor: "pointer" },
  rowLabel: { fontWeight: 600, fontSize: 13.5, flex: 1 },
  rowMeta: { fontSize: 11.5, color: "#6b7280" },
  pctChip: (pct) => ({ padding: "3px 10px", borderRadius: 999, fontSize: 12, fontWeight: 700, background: pctBg(pct) }),
  elRow: { display: "flex", alignItems: "center", gap: 10, padding: "9px 12px", borderRadius: 8,
           border: "1px solid #e5e7eb" },
  dot: (done) => ({ width: 10, height: 10, borderRadius: "50%", background: done ? "#16a34a" : "#dc2626", flexShrink: 0 }),
  helpBtn: { fontSize: 11, color: "#6b7280", cursor: "help" },
  linkBtn: { fontSize: 12, fontWeight: 600, color: "#2563eb", background: "none", border: "none", cursor: "pointer" },
  empty: { fontSize: 13, color: "#9ca3af", padding: "24px 0", textAlign: "center" },
  naRow: { opacity: 0.6 },
  naBadge: { display: "inline-block", padding: "2px 8px", borderRadius: 999, fontSize: 11, fontWeight: 600,
             background: "#f1f5f9", color: "#64748b", border: "1px solid #e2e8f0" },
  naBar: { display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 12px", borderRadius: 8,
           background: "#f8fafc", border: "1px solid #e2e8f0", marginBottom: 10, fontSize: 12.5, color: "#475569" },
  naForm: { display: "flex", flexDirection: "column", gap: 6, marginTop: 8 },
  naInput: { padding: "7px 10px", fontSize: 12.5, border: "1px solid #d1d5db", borderRadius: 6, fontFamily: "inherit" },
  naActions: { display: "flex", gap: 8 },
  btnSmall: { padding: "6px 12px", fontSize: 12, fontWeight: 600, border: "1px solid #d1d5db", borderRadius: 6,
              background: "#fff", cursor: "pointer" },
  btnSmallPrimary: { padding: "6px 12px", fontSize: 12, fontWeight: 600, border: "none", borderRadius: 6,
                     background: "#1e293b", color: "#fff", cursor: "pointer" },
};

// Small inline control, shown above the evidence-category list, for marking
// the active quality statement not-applicable (or applicable again) to the
// active site — the mechanism cqcTaxonomy.js's header describes for
// mixed-sector organisations. Deliberately manual: a reason is required to
// mark something not applicable, so the call is a recorded human judgement,
// not an inferred one.
function ApplicabilityBar({ record, onSet }) {
  const [editing, setEditing] = useState(false);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const notApplicable = record && record.applicable === false;

  if (!notApplicable && !editing) {
    return (
      <div style={{ marginBottom: 10 }}>
        <button style={S.linkBtn} onClick={() => setEditing(true)}>Mark not applicable to this site…</button>
      </div>
    );
  }

  if (editing && !notApplicable) {
    return (
      <div style={S.naBar}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Why doesn't this statement apply to this site?</div>
          <div style={S.naForm}>
            <input style={S.naInput} placeholder="e.g. Site holds no regulated activity this statement concerns"
                   value={reason} onChange={(e) => setReason(e.target.value)} />
            <div style={S.naActions}>
              <button style={S.btnSmallPrimary} disabled={!reason.trim() || saving}
                      onClick={async () => { setSaving(true); await onSet(false, reason.trim()); setSaving(false); setEditing(false); setReason(""); }}>
                {saving ? "Saving…" : "Confirm not applicable"}
              </button>
              <button style={S.btnSmall} onClick={() => { setEditing(false); setReason(""); }}>Cancel</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={S.naBar}>
      <div style={{ flex: 1 }}>
        <div><span style={S.naBadge}>Not applicable to this site</span></div>
        <div style={{ marginTop: 4 }}>{record.reason}</div>
        {record.recordedBy && (
          <div style={{ marginTop: 4, color: "#94a3b8" }}>
            Set by {record.recordedBy}{record.recordedAt ? ` on ${new Date(record.recordedAt).toLocaleDateString()}` : ""}
          </div>
        )}
        <div style={{ marginTop: 8 }}>
          <button style={S.btnSmall} disabled={saving}
                  onClick={async () => { setSaving(true); await onSet(true, ""); setSaving(false); }}>
            {saving ? "Saving…" : "This does apply — restore it"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function CqcCompliance({ initialSiteId }) {
  const [sites, setSites] = useState([]);
  const [attestations, setAttestations] = useState([]);
  const [applicability, setApplicabilityRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [groupId, setGroupId] = useState(GROUPS[0].id);
  const [drill, setDrill] = useState({ level: "items" }); // items | sites | elements
  const [activeItemId, setActiveItemId] = useState(null);
  const [activeSiteId, setActiveSiteId] = useState(initialSiteId || null);
  const [dialogEl, setDialogEl] = useState(null); // { itemId, element }

  async function load() {
    setLoading(true); setError(null);
    try {
      const [sitesRes, attRes, appRes] = await Promise.all([
        cqcApi.listSites(), cqcApi.listAttestations(), cqcApi.listApplicability(),
      ]);
      setSites(sitesRes.sites || []);
      setAttestations(attRes.attestations || []);
      setApplicabilityRecords(appRes.applicability || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const attByKey = useMemo(() => attestationIndex(attestations), [attestations]);
  const appByKey = useMemo(() => applicabilityIndex(applicability), [applicability]);

  async function setItemApplicability(siteId, itemGroupId, itemId, applicable, reason) {
    await cqcApi.setApplicability({ siteId, groupId: itemGroupId, itemId, applicable, reason });
    await load();
  }

  const group = GROUPS.find((g) => g.id === groupId);

  function openGroup(gid) {
    setGroupId(gid);
    setDrill({ level: "items" });
    setActiveItemId(null);
    setActiveSiteId(initialSiteId || null);
  }
  function openItem(itemId) {
    setActiveItemId(itemId);
    if (initialSiteId) { setActiveSiteId(initialSiteId); setDrill({ level: "elements" }); }
    else setDrill({ level: "sites" });
  }
  function openSite(siteId) {
    setActiveSiteId(siteId);
    setDrill({ level: "elements" });
  }

  const activeItem = activeItemId && group ? group.items.find((it) => it.id === activeItemId) : null;
  const activeSite = activeSiteId ? sites.find((s) => s._id === activeSiteId) : null;

  if (loading) return <div style={S.wrap}>Loading CQC compliance data…</div>;
  if (error) return <div style={{ ...S.wrap, color: "#991b1b" }}>Couldn't load compliance data: {error}</div>;
  if (sites.length === 0) {
    return <div style={S.wrap}><div style={S.empty}>No sites connected yet — import your CQC-registered sites first.</div></div>;
  }

  return (
    <div style={S.wrap}>
      <div style={S.header}><h1 style={S.h1}>CQC Compliance</h1></div>
      <div style={S.sub}>{sites.length} site{sites.length === 1 ? "" : "s"} · 5 key questions · 34 quality statements</div>

      <div style={S.tabs}>
        {GROUPS.map((g) => {
          const pct = groupPctAcrossSites(attByKey, sites, g, appByKey);
          return (
            <div key={g.id} style={{ ...S.tab(g.id === groupId), background: pctBgStrong(pct) }}
                 onClick={() => openGroup(g.id)}>
              {g.label} · {fmtPct(pct)}
            </div>
          );
        })}
      </div>

      {/* Breadcrumb */}
      <div style={S.crumbs}>
        <span style={S.crumbLink} onClick={() => setDrill({ level: "items" })}>{group.label}</span>
        {activeItem && drill.level !== "items" && (
          <> {" › "} <span style={activeSite ? S.crumbLink : undefined}
                             onClick={() => setDrill({ level: "sites" })}>{activeItem.label}</span></>
        )}
        {activeSite && drill.level === "elements" && <> {" › "} {activeSite.name}</>}
      </div>

      {drill.level === "items" && (
        <div style={S.list}>
          <p style={{ fontSize: 12.5, color: "#6b7280", margin: "0 0 4px" }}>{group.description}</p>
          {group.items.map((it) => {
            const pct = itemPctAcrossSites(attByKey, sites, group.id, it, appByKey);
            return (
              <div key={it.id} style={S.row} onClick={() => openItem(it.id)}>
                <div style={S.rowLabel}>{it.label}</div>
                <div style={S.pctChip(pct)}>{fmtPct(pct)}</div>
              </div>
            );
          })}
        </div>
      )}

      {drill.level === "sites" && activeItem && (
        <div style={S.list}>
          <p style={{ fontSize: 12.5, color: "#6b7280", margin: "0 0 4px" }}>{activeItem.description}</p>
          {sites.map((s) => {
            const applicable = itemApplicableForSite(appByKey, s._id, group.id, activeItem.id);
            const pct = itemPctForSite(attByKey, s._id, group.id, activeItem, appByKey);
            return (
              <div key={s._id} style={{ ...S.row, ...(applicable ? {} : S.naRow) }} onClick={() => openSite(s._id)}>
                <div style={S.rowLabel}>{s.name}</div>
                {s.ratings && <RatingBadge rating={s.ratings.overall} />}
                {applicable
                  ? <div style={S.pctChip(pct)}>{fmtPct(pct)}</div>
                  : <span style={S.naBadge}>Not applicable</span>}
              </div>
            );
          })}
        </div>
      )}

      {drill.level === "elements" && activeItem && activeSite && (
        <div style={S.list}>
          <p style={{ fontSize: 12.5, color: "#6b7280", margin: "0 0 4px" }}>
            {activeItem.description}
          </p>
          <ApplicabilityBar
            record={appByKey[`${activeSite._id}|${group.id}|${activeItem.id}`]}
            onSet={(applicable, reason) => setItemApplicability(activeSite._id, group.id, activeItem.id, applicable, reason)}
          />
          {!itemApplicableForSite(appByKey, activeSite._id, group.id, activeItem.id) && (
            <div style={{ fontSize: 12, color: "#94a3b8", margin: "-4px 0 4px" }}>
              Evidence categories below are shown for reference but excluded from this site's percentage while marked not applicable.
            </div>
          )}
          {activeItem.elements.map((el) => {
            const att = attByKey[`${activeSite._id}|${group.id}|${activeItem.id}|${el.id}`];
            const done = !!att;
            const applicable = itemApplicableForSite(appByKey, activeSite._id, group.id, activeItem.id);
            return (
              <div key={el.id} style={{ ...S.elRow, ...(applicable ? {} : S.naRow) }}>
                <div style={S.dot(done)} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>
                    {el.label} <ReqBadge requirement={el.requirement} />
                  </div>
                  <div style={S.rowMeta} title={el.help}>
                    {done
                      ? `${att.source}${att.documentRef && att.documentRef.url ? ` — ${att.documentRef.url}` : ""}`
                      : "No evidence source recorded yet"}
                  </div>
                </div>
                <button style={S.linkBtn} onClick={() => setDialogEl(el)}>
                  {done ? "Review" : "Set source"}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {dialogEl && activeItem && activeSite && (
        <CqcEvidenceDialog
          site={activeSite}
          groupId={group.id}
          itemId={activeItem.id}
          element={dialogEl}
          existing={attByKey[`${activeSite._id}|${group.id}|${activeItem.id}|${dialogEl.id}`]}
          onClose={() => setDialogEl(null)}
          onSaved={load}
        />
      )}
    </div>
  );
}
